/**
 * AcenteRoomList — printable room-allocation sheet.
 *
 * Purpose
 * -------
 * Agencies frequently need a paper "who sleeps where" list at the hotel
 * check-in desk. Given a reservation's room breakdown (1K/2K/3K/4K/5K),
 * this page renders one row per bed, lets the agency type each pilgrim's
 * name, and hits print/PDF.
 *
 * Data model (client-side only, per session):
 *   picked_reservation_id?
 *   header : { group_name, hotel_name, checkin, checkout, staff_contact }
 *   rooms  : [{ number, type: "1K"|..., beds: [{ name, note }] }]
 *
 * Rooms are auto-generated from the reservation's rooms breakdown when the
 * agency picks a reservation; they can rename, add or remove rooms manually.
 *
 * Print uses window.print() + a scoped `.print-area` block.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BedDouble, Printer, Plus, Trash2, ClipboardList, Hotel, User, Phone, Wand2,
} from "lucide-react";

const ROOM_TYPE_CAPACITY = { "1K": 1, "2K": 2, "3K": 3, "4K": 4, "5K": 5 };

function buildRoomsFromBreakdown(rb) {
  // rb = { r1, r2, r3, r4, r5 }
  const list = [];
  const map = [["r1", "1K"], ["r2", "2K"], ["r3", "3K"], ["r4", "4K"], ["r5", "5K"]];
  let counter = 1;
  for (const [key, type] of map) {
    const cap = ROOM_TYPE_CAPACITY[type];
    for (let i = 0; i < (rb?.[key] || 0); i++) {
      list.push({
        number: `${counter}`,
        type,
        beds: Array.from({ length: cap }, () => ({ name: "", note: "" })),
      });
      counter++;
    }
  }
  return list;
}

function defaultHeader() {
  return {
    group_name: "",
    hotel_name: "",
    checkin: "",
    checkout: "",
    staff_contact: "",
  };
}

export default function AcenteRoomList() {
  // Persist typed room allocations across refresh — high-value for agencies
  // who spend time entering pilgrim names one by one.
  const STORAGE_KEY = "ariaf_room_list_draft_v1";

  const [reservations, setReservations] = useState([]);
  const [pickedResId, setPickedResId] = useState("");
  const [header, setHeader] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw).header || defaultHeader();
    } catch {}
    return defaultHeader();
  });
  const [rooms, setRooms] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw).rooms || [];
    } catch {}
    return [];
  });
  const printRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ header, rooms })); } catch {}
  }, [header, rooms]);

  const resetDraft = () => {
    if (!window.confirm("Tüm alanlar sıfırlanacak. Emin misiniz?")) return;
    setHeader(defaultHeader());
    setRooms([]);
    setPickedResId("");
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  useEffect(() => {
    panelApi.get("/reservations").then((r) => setReservations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setReservations([]));
  }, []);

  const totalBeds = useMemo(() => rooms.reduce((n, r) => n + r.beds.length, 0), [rooms]);
  const totalFilled = useMemo(() => rooms.reduce((n, r) => n + r.beds.filter((b) => b.name.trim()).length, 0), [rooms]);

  const applyReservation = (rid) => {
    setPickedResId(rid);
    const r = reservations.find((x) => x.id === rid);
    if (!r) return;
    setHeader((h) => ({
      ...h,
      group_name: r.group_name || h.group_name,
      hotel_name: r.hotel_name || h.hotel_name,
      checkin: r.checkin || h.checkin,
      checkout: r.checkout || h.checkout,
    }));
    setRooms(buildRoomsFromBreakdown(r.rooms));
    toast.success("Rezervasyondan oda dağılımı yüklendi");
  };

  const setHField = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const setRoomNumber = (idx, number) => setRooms((prev) => prev.map((r, i) => (i === idx ? { ...r, number } : r)));
  const setBed = (roomIdx, bedIdx, patch) =>
    setRooms((prev) => prev.map((r, i) => i !== roomIdx ? r : { ...r, beds: r.beds.map((b, j) => (j === bedIdx ? { ...b, ...patch } : b)) }));

  const changeRoomType = (idx, newType) => {
    const cap = ROOM_TYPE_CAPACITY[newType] || 1;
    setRooms((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const beds = [...r.beds];
      while (beds.length < cap) beds.push({ name: "", note: "" });
      while (beds.length > cap) beds.pop();
      return { ...r, type: newType, beds };
    }));
  };

  const addRoom = (type = "2K") => setRooms((prev) => [
    ...prev,
    { number: `${prev.length + 1}`, type, beds: Array.from({ length: ROOM_TYPE_CAPACITY[type] }, () => ({ name: "", note: "" })) },
  ]);

  const removeRoom = (idx) => setRooms((prev) => prev.filter((_, i) => i !== idx));

  const clearAllNames = () => {
    if (!window.confirm("Girilen tüm isimler silinecek. Emin misiniz?")) return;
    setRooms((prev) => prev.map((r) => ({ ...r, beds: r.beds.map(() => ({ name: "", note: "" })) })));
  };

  const handlePrint = () => setTimeout(() => window.print(), 100);

  return (
    <div className="space-y-6" data-testid="acente-room-list-page">
      <div className="print:hidden">
        <header>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2 inline-flex items-center gap-1">
            <ClipboardList size={12} /> Yardımcı Araç
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-serif text-3xl text-brand-navy">Oda Listesi (Müşteri Ataması)</h1>
              <p className="text-sm text-slate-600 mt-1">
                Her odaya kalacak müşterilerin isimlerini yazın, oda numaralarını düzenleyin ve
                sağ üstteki butondan çıktı alın. Otel resepsiyonuna ve rehberinize verebilirsiniz.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetDraft}
                className="text-slate-600 border-slate-200 hover:bg-slate-50"
                data-testid="rooms-reset"
                title="Tüm alanları sıfırla"
              >
                Sıfırla
              </Button>
              <Button
                type="button"
                onClick={handlePrint}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark"
                data-testid="rooms-print"
              >
                <Printer size={14} className="mr-1.5" /> Yazdır / PDF
              </Button>
            </div>
          </div>
        </header>

        {/* Prefill from reservation */}
        <div className="bg-gradient-to-br from-brand-orange/5 via-white to-brand-gold/5 border border-brand-orange/20 rounded-2xl p-4 mt-4" data-testid="rooms-prefill">
          <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">Hızlı Doldur</div>
          <div className="text-sm text-slate-700 mt-1">
            Bir rezervasyon seçin; oda dağılımı (1K / 2K / 3K / 4K / 5K) otomatik yüklensin:
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <select
              value={pickedResId}
              onChange={(e) => applyReservation(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-transparent px-3 py-2 text-sm flex-1 min-w-[220px]"
              data-testid="rooms-reservation-picker"
            >
              <option value="">— Rezervasyon seçin —</option>
              {reservations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.reservation_number} · {r.group_name} · {formatTr(r.checkin)} → {formatTr(r.checkout)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Header inputs */}
        <div className="bg-white border border-brand-navy/10 rounded-2xl p-5 mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="flex items-center gap-1"><User size={12}/> Grup Adı</Label>
            <Input value={header.group_name} onChange={(e) => setHField("group_name", e.target.value)} data-testid="rooms-group-name" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Hotel size={12}/> Otel</Label>
            <Input value={header.hotel_name} onChange={(e) => setHField("hotel_name", e.target.value)} data-testid="rooms-hotel" />
          </div>
          <div>
            <Label>Giriş Tarihi</Label>
            <Input type="date" value={header.checkin} onChange={(e) => setHField("checkin", e.target.value)} data-testid="rooms-checkin" />
          </div>
          <div>
            <Label>Çıkış Tarihi</Label>
            <Input type="date" value={header.checkout} onChange={(e) => setHField("checkout", e.target.value)} data-testid="rooms-checkout" />
          </div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1"><Phone size={12}/> Sorumlu / İletişim</Label>
            <Input value={header.staff_contact} onChange={(e) => setHField("staff_contact", e.target.value)} placeholder="Rehber adı ve telefonu" data-testid="rooms-contact" />
          </div>
        </div>

        {/* Editor toolbar */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-xs text-slate-500 mr-1">Manuel oda ekle:</span>
          {["1K", "2K", "3K", "4K", "5K"].map((t) => (
            <Button
              key={t}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addRoom(t)}
              className="text-xs border-brand-orange/30 hover:bg-brand-orange/10"
              data-testid={`rooms-add-${t}`}
            >
              <Plus size={12} className="mr-1" /> {t}
            </Button>
          ))}
          {rooms.length > 0 && (
            <>
              <div className="ml-auto text-xs text-slate-500">
                Toplam yatak: <b className="text-brand-navy">{totalBeds}</b> · Yazılı: <b className="text-brand-navy">{totalFilled}</b> / {totalBeds}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={clearAllNames}
                className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid="rooms-clear-all"
              >
                <Wand2 size={12} className="mr-1" /> Tüm isimleri sil
              </Button>
            </>
          )}
        </div>

        {/* Rooms editor */}
        <div className="mt-4 grid gap-3" data-testid="rooms-editor">
          {rooms.length === 0 && (
            <div className="bg-white border border-dashed border-brand-navy/20 rounded-2xl p-10 text-center text-slate-500 text-sm">
              Henüz oda yok — üstten bir rezervasyon seçin ya da manuel olarak oda ekleyin.
            </div>
          )}
          {rooms.map((r, i) => (
            <div key={i} className="bg-white border border-brand-navy/10 rounded-2xl p-4" data-testid={`rooms-room-${i}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Oda No</Label>
                  <Input
                    value={r.number}
                    onChange={(e) => setRoomNumber(i, e.target.value)}
                    className="w-24 h-9"
                    data-testid={`rooms-room-number-${i}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Tip</Label>
                  <select
                    value={r.type}
                    onChange={(e) => changeRoomType(i, e.target.value)}
                    className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
                    data-testid={`rooms-room-type-${i}`}
                  >
                    {Object.keys(ROOM_TYPE_CAPACITY).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <span className="text-xs text-slate-500">({r.beds.length} kişi)</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => removeRoom(i)}
                  className="ml-auto text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                  data-testid={`rooms-remove-${i}`}
                >
                  <Trash2 size={12} className="mr-1" /> Odayı Sil
                </Button>
              </div>
              <div className="mt-3 grid gap-2">
                {r.beds.map((b, j) => (
                  <div key={j} className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center">
                    <div className="text-xs text-slate-500 pl-2">Yatak {j + 1}</div>
                    <Input
                      value={b.name}
                      onChange={(e) => setBed(i, j, { name: e.target.value })}
                      placeholder="Müşteri adı"
                      className="h-9"
                      data-testid={`rooms-bed-name-${i}-${j}`}
                    />
                    <Input
                      value={b.note}
                      onChange={(e) => setBed(i, j, { note: e.target.value })}
                      placeholder="Not (opsiyonel)"
                      className="h-9"
                      data-testid={`rooms-bed-note-${i}-${j}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Print-only preview */}
      <div ref={printRef} className="print-area hidden print:block" data-testid="rooms-print-preview">
        <RoomsPrintPreview header={header} rooms={rooms} totalBeds={totalBeds} totalFilled={totalFilled} />
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .print-area { display: block !important; }
          .print-area .room-block { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function RoomsPrintPreview({ header, rooms, totalBeds, totalFilled }) {
  return (
    <div className="text-brand-navy">
      <header className="border-b-2 border-brand-navy/40 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-brand-orange font-semibold">Oda Listesi</div>
            <h1 className="font-serif text-2xl mt-1">{header.group_name || "—"}</h1>
            <div className="mt-1 text-sm text-slate-700">
              {header.hotel_name && <><b>Otel:</b> {header.hotel_name}</>}
              {header.checkin && <> · <b>Giriş:</b> {formatTr(header.checkin)}</>}
              {header.checkout && <> · <b>Çıkış:</b> {formatTr(header.checkout)}</>}
            </div>
            {header.staff_contact && <div className="text-sm text-slate-700"><b>Sorumlu:</b> {header.staff_contact}</div>}
          </div>
          <div className="text-right text-xs text-slate-600">
            <div>Oda sayısı: <b>{rooms.length}</b></div>
            <div>Toplam yatak: <b>{totalBeds}</b></div>
            <div>Yazılı isim: <b>{totalFilled}</b> / {totalBeds}</div>
          </div>
        </div>
      </header>

      <div className="mt-4 grid gap-3">
        {rooms.map((r, i) => (
          <div key={i} className="room-block border border-brand-navy/25 rounded-lg overflow-hidden">
            <div className="bg-brand-navy text-white px-3 py-1.5 flex items-center justify-between text-sm">
              <div className="font-serif text-base">Oda {r.number || (i + 1)} · {r.type}</div>
              <div className="text-brand-gold text-xs uppercase tracking-widest">{r.beds.length} kişi</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-cream text-left">
                  <th className="px-3 py-1.5 w-16 font-serif text-brand-navy">#</th>
                  <th className="px-3 py-1.5 font-serif text-brand-navy">Ad Soyad</th>
                  <th className="px-3 py-1.5 w-1/3 font-serif text-brand-navy">Not</th>
                </tr>
              </thead>
              <tbody>
                {r.beds.map((b, j) => (
                  <tr key={j} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-slate-400 font-mono">{j + 1}</td>
                    <td className="px-3 py-1.5">{b.name || <span className="text-slate-300 italic">………………………………</span>}</td>
                    <td className="px-3 py-1.5 text-slate-600">{b.note || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <footer className="mt-6 pt-3 border-t border-slate-300 text-[10px] text-slate-500 text-center">
        Ariaf Catering · Umre yolcularına vatan sofrası
      </footer>
    </div>
  );
}
